import { Component, computed, inject, input, output } from '@angular/core';
import { IonicModule, ModalController } from '@ionic/angular';
import { AuthStateService } from '../../app/auth/auth-state.service';
import {
    ShowFilter,
    ShowFilterModalComponent
} from '../../app/showreport/showreport-list/show-filter-modal/show-filter-modal.component';

/**
 * Shared filter bar for the showreport / logboek / klussenlijst list
 * pages. Renders the current filter state as chips and a "Filter" button
 * that opens {@link ShowFilterModalComponent}; emits the new filter when
 * the user confirms a change. Owning the modal-opening + chip-removal
 * logic in one place keeps the three list pages from drifting and lets
 * future filter-UX changes happen exactly once.
 *
 * Parents only need to:
 *  - bind `[currentFilter]` to their persisted filter signal value
 *  - listen for `(filterChange)` and persist + reload on each emission
 *
 * The visual behaviour (active-show grey chip, all-shows dismissable
 * chip, selected-shows multi-chip row) matches the previous per-page
 * implementations exactly so the user-visible chip layout is unchanged.
 */
@Component({
    selector: 'app-show-filter-bar',
    templateUrl: './show-filter-bar.component.html',
    styleUrls: ['./show-filter-bar.component.scss'],
    imports: [IonicModule]
})
export class ShowFilterBarComponent {

    /** Current filter to display. Required. */
    public readonly currentFilter = input.required<ShowFilter>();

    /**
     * Theme colour for the filter button and "active" chips. Matches the
     * host page's accent: showreports use `secondary`, logboek and tasks
     * use `primary`.
     */
    public readonly color = input<'primary' | 'secondary'>('primary');

    /** Emits whenever the filter changes (modal confirm, chip remove, clear). */
    public readonly filterChange = output<ShowFilter>();

    private readonly modalController = inject(ModalController);
    private readonly authStateService = inject(AuthStateService);

    public readonly isFilterActive = computed(
        () => this.currentFilter().displayMode !== 'active-show'
    );

    public readonly filterIcon = computed(
        () => this.isFilterActive() ? 'funnel' : 'funnel-outline'
    );

    public readonly activeShowName = computed(
        () => this.authStateService.activeShow()?.name ?? 'Actieve productie'
    );

    /** Chips to render when in `selected-shows` mode. */
    public readonly selectedShowChips = computed(() => {
        const filter = this.currentFilter();
        if (filter.displayMode !== 'selected-shows') {
            return [];
        }
        const allShows = this.authStateService.availableShows();
        return filter.selectedShowIds
            .map(id => allShows.find(s => s.id === id))
            .filter((show): show is NonNullable<typeof show> => show !== undefined)
            .map(show => ({ id: show.id, name: show.name }));
    });

    public async openFilterModal(): Promise<void> {
        const modal = await this.modalController.create({
            component: ShowFilterModalComponent,
            componentProps: {
                currentFilter: this.currentFilter()
            }
        });

        await modal.present();
        const { data, role } = await modal.onWillDismiss();

        if (role === 'confirm' && data) {
            this.filterChange.emit(data as ShowFilter);
        }
    }

    /**
     * Remove one show from the current `selected-shows` filter. If it
     * was the last one, fall back to the active-show default.
     */
    public removeShowFromFilter(showId: number): void {
        const filter = this.currentFilter();
        const remainingIds = filter.selectedShowIds.filter(id => id !== showId);

        if (remainingIds.length === 0) {
            this.clearFilter();
        } else {
            this.filterChange.emit({
                displayMode: 'selected-shows',
                selectedShowIds: remainingIds
            });
        }
    }

    /** Reset the filter to the default `active-show` mode. */
    public clearFilter(): void {
        this.filterChange.emit({
            displayMode: 'active-show',
            selectedShowIds: []
        });
    }
}
